import { useState, useMemo, useCallback } from "react"
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
  type ColumnSizingState,
  type RowData,
} from "@tanstack/react-table"
import { ArrowDownSvg, ArrowTopSvg } from "~/ui-rtk/components/ui/svg/arrow"
import Checkbox from "~/ui-rtk/components/ui/controls/Checkbox"
import { formatNumber } from "~/ui-rtk/utils/format"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import DeltaCell from "./DeltaCell"
import MomentumBadge from "./MomentumBadge"
import type { AdsetRow } from "../types"

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: "left" | "center" | "right"
  }
}

const STATUS_BADGE_COLORS: Record<string, string> = {
  ACTIVE: "bg-green-600/30 text-green-300",
  PAUSED: "bg-gray-600/30 text-gray-300",
}

const COLUMN_SIZING_STORAGE_KEY = "creative-management-column-sizing"

function formatDateDisplay(dateStr: string): string {
  const [year, month, day] = dateStr.split("-").map(Number)
  const date = new Date(year, month - 1, day)
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

const columnHelper = createColumnHelper<AdsetRow>()

interface AdsetTableProps {
  rows: AdsetRow[]
  selectedIds: Set<string>
  dataThrough: string | null
  onToggleSelect: (adsetId: string) => void
  onToggleSelectAll: () => void
}

export default function AdsetTable({
  rows,
  selectedIds,
  dataThrough,
  onToggleSelect,
  onToggleSelectAll,
}: AdsetTableProps) {
  const allSelected = rows.length > 0 && selectedIds.size === rows.length

  const [sorting, setSorting] = useState<SortingState>([
    { id: "spend7", desc: true },
  ])

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() => {
    try {
      const stored = localStorage.getItem(COLUMN_SIZING_STORAGE_KEY)
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  })

  const handleColumnSizingChange = useCallback(
    (
      updater:
        | ColumnSizingState
        | ((prev: ColumnSizingState) => ColumnSizingState),
    ) => {
      setColumnSizing(prev => {
        const next = typeof updater === "function" ? updater(prev) : updater
        localStorage.setItem(COLUMN_SIZING_STORAGE_KEY, JSON.stringify(next))
        return next
      })
    },
    [],
  )

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        size: 48,
        minSize: 48,
        maxSize: 48,
        enableSorting: false,
        enableResizing: false,
        header: () => (
          <Checkbox
            checked={allSelected}
            onChange={onToggleSelectAll}
            className="checkbox-sm"
          />
        ),
        cell: info => (
          <Checkbox
            checked={selectedIds.has(info.row.original.adsetId)}
            onChange={() => onToggleSelect(info.row.original.adsetId)}
            className="checkbox-sm"
          />
        ),
      }),
      columnHelper.accessor("adsetName", {
        id: "adsetName",
        header: "Ad Set",
        size: 280,
        minSize: 140,
        enableSorting: true,
        enableResizing: true,
        cell: info => {
          const row = info.row.original
          return (
            <div className="flex flex-col min-w-0">
              <span className="text-white text-sm truncate">
                {row.adsetName}
              </span>
              <span className="text-xs text-gray-500 truncate">
                {row.campaignName}
              </span>
            </div>
          )
        },
      }),
      columnHelper.accessor("status", {
        id: "status",
        header: "Status",
        size: 100,
        minSize: 80,
        enableSorting: true,
        enableResizing: true,
        cell: info => {
          const badgeColor =
            STATUS_BADGE_COLORS[info.getValue()] ??
            "bg-gray-600/30 text-gray-300"
          return (
            <span
              className={cn(
                "text-xs px-2 py-0.5 rounded-full font-medium",
                badgeColor,
              )}
            >
              {info.getValue() || "--"}
            </span>
          )
        },
      }),
      columnHelper.accessor("spend7", {
        id: "spend7",
        header: "Spend 7d",
        size: 120,
        minSize: 90,
        enableSorting: true,
        enableResizing: true,
        meta: { align: "right" },
        cell: info => (
          <DeltaCell
            value={formatNumber(info.getValue(), { currency: true })}
            delta={info.row.original.spendDeltaPct}
          />
        ),
      }),
      columnHelper.accessor("activeAds", {
        id: "activeAds",
        header: "Active Ads",
        size: 110,
        minSize: 80,
        enableSorting: true,
        enableResizing: true,
        meta: { align: "right" },
        cell: info => (
          <DeltaCell
            value={String(info.getValue())}
            delta={info.row.original.activeAdsDelta}
            format="count"
          />
        ),
      }),
      columnHelper.accessor(row => row.avgDailySpendPerActiveAd ?? -1, {
        id: "avgDailySpendPerActiveAd",
        header: "$/Day/Ad",
        size: 110,
        minSize: 80,
        enableSorting: true,
        enableResizing: true,
        meta: { align: "right" },
        cell: info => {
          const row = info.row.original
          if (row.avgDailySpendPerActiveAd === null) {
            return <span className="text-sm text-gray-500">--</span>
          }
          return (
            <DeltaCell
              value={formatNumber(row.avgDailySpendPerActiveAd, {
                currency: true,
              })}
              delta={row.avgDailySpendDeltaPct}
            />
          )
        },
      }),
      columnHelper.accessor(row => row.top5Share ?? -1, {
        id: "top5Share",
        header: "Top-5 Concentration",
        size: 150,
        minSize: 110,
        enableSorting: true,
        enableResizing: true,
        meta: { align: "right" },
        cell: info => {
          const row = info.row.original
          if (row.top5Share === null) {
            return <span className="text-sm text-gray-500">--</span>
          }
          return (
            <DeltaCell
              value={`${(row.top5Share * 100).toFixed(1)}%`}
              delta={row.top5ShareDeltaPp}
              format="pp"
              goodDirection="down"
            />
          )
        },
      }),
      columnHelper.accessor(row => row.modeledRoas ?? -1, {
        id: "modeledRoas",
        header: "Modeled ROAS",
        size: 120,
        minSize: 90,
        enableSorting: true,
        enableResizing: true,
        meta: { align: "right" },
        cell: info => {
          const row = info.row.original
          if (row.modeledRoas === null) {
            return <span className="text-sm text-gray-500">--</span>
          }
          return (
            <DeltaCell
              value={`${row.modeledRoas.toFixed(2)}x`}
              delta={row.modeledRoasDeltaPct}
            />
          )
        },
      }),
      columnHelper.accessor(row => row.modeledBroas ?? -1, {
        id: "modeledBroas",
        header: "Modeled BROAS",
        size: 120,
        minSize: 90,
        enableSorting: true,
        enableResizing: true,
        meta: { align: "right" },
        cell: info => {
          const row = info.row.original
          if (row.modeledBroas === null) {
            return <span className="text-sm text-gray-500">--</span>
          }
          return (
            <DeltaCell
              value={`${row.modeledBroas.toFixed(2)}x`}
              delta={row.modeledBroasDeltaPct}
            />
          )
        },
      }),
      columnHelper.accessor("momentum", {
        id: "momentum",
        header: "Momentum",
        size: 110,
        minSize: 90,
        enableSorting: true,
        enableResizing: true,
        cell: info => (
          <MomentumBadge
            momentum={info.getValue()}
            signals={info.row.original.momentumSignals}
          />
        ),
      }),
    ],
    [allSelected, selectedIds, onToggleSelect, onToggleSelectAll],
  )

  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting,
      columnSizing,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    onColumnSizingChange: handleColumnSizingChange,
  })

  const summary = useMemo(() => {
    const totalSpend = rows.reduce((s, r) => s + r.spend7, 0)
    const totalActiveAds = rows.reduce((s, r) => s + r.activeAds, 0)
    const totalModeledRevenue = rows.reduce((s, r) => s + r.modeledRevenue7, 0)
    const totalBrandValue = rows.reduce((s, r) => s + r.brandValue7, 0)
    return {
      totalSpend,
      totalActiveAds,
      weightedRoas: totalSpend > 0 ? totalModeledRevenue / totalSpend : null,
      weightedBroas: totalSpend > 0 ? totalBrandValue / totalSpend : null,
    }
  }, [rows])

  const renderSummaryCell = useCallback(
    (colId: string) => {
      switch (colId) {
        case "adsetName":
          return "Total"
        case "spend7":
          return formatNumber(summary.totalSpend, { currency: true })
        case "activeAds":
          return String(summary.totalActiveAds)
        case "modeledRoas":
          return summary.weightedRoas !== null
            ? `${summary.weightedRoas.toFixed(2)}x`
            : "--"
        case "modeledBroas":
          return summary.weightedBroas !== null
            ? `${summary.weightedBroas.toFixed(2)}x`
            : "--"
        default:
          return null
      }
    },
    [summary],
  )

  if (rows.length === 0) {
    return (
      <div className="border border-basic-blue rounded-lg p-8 text-center text-gray-400">
        No Meta ad sets with recent spend or ACTIVE status found.
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-end mb-2">
        <span className="text-sm text-gray-400">
          Data Through:{" "}
          <span className="text-gray-300">
            {dataThrough ? formatDateDisplay(dataThrough) : "--"}
          </span>
        </span>
      </div>
      <div className="border border-basic-blue rounded-lg overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const isSorted = header.column.getIsSorted()
                  const canSort = header.column.getCanSort()
                  const align = header.column.columnDef.meta?.align ?? "left"

                  return (
                    <th
                      key={header.id}
                      className={cn(
                        "relative px-3 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wider bg-basic-dark-blue group select-none",
                        canSort && "cursor-pointer",
                        align === "right" && "text-right",
                      )}
                      style={{ width: header.getSize() }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div
                        className={cn(
                          "flex items-center gap-1",
                          align === "right" && "justify-end",
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        {isSorted === "desc" && <ArrowDownSvg size={14} />}
                        {isSorted === "asc" && <ArrowTopSvg size={14} />}
                        {!isSorted && canSort && (
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <ArrowDownSvg size={14} />
                          </span>
                        )}
                      </div>
                      {header.column.getCanResize() && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          onClick={e => e.stopPropagation()}
                          className="hover:bg-blue-400/50"
                          style={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                            height: "100%",
                            width: "6px",
                            cursor: "col-resize",
                            userSelect: "none",
                            touchAction: "none",
                            zIndex: 10,
                            background: header.column.getIsResizing()
                              ? "#60a5fa"
                              : "transparent",
                            transition: "background 0.2s",
                          }}
                        />
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                className="border-t border-basic-blue hover:bg-system-blue-800/50 transition-colors"
              >
                {row.getVisibleCells().map(cell => {
                  const align = cell.column.columnDef.meta?.align ?? "left"
                  return (
                    <td
                      key={cell.id}
                      className={cn(
                        "px-3 py-2",
                        align === "right" && "text-right",
                      )}
                      style={{ width: cell.column.getSize() }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-system-blue-800 border-t border-basic-blue">
              {table.getHeaderGroups()[0].headers.map(header => {
                const colId = header.column.id
                const align = header.column.columnDef.meta?.align ?? "left"
                return (
                  <td
                    key={colId}
                    className={cn(
                      "px-3 py-3 text-sm font-semibold text-white",
                      align === "right" && "text-right",
                    )}
                    style={{ width: header.getSize() }}
                  >
                    {renderSummaryCell(colId)}
                  </td>
                )
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}
